import {
	Button,
	ButtonSpinner,
	Form,
	Input,
	Locale,
	Slideout,
	SmallText,
} from '@local/ui/components'
import {cx} from '@local/utils/cx'
import PersonSVG from '@local/assets/svgs/icon-person.svg'
import BedSVG from '@local/assets/svgs/icon-bed.svg'
import CloseSVG from '@local/assets/svgs/icon-close.svg'
import DateSVG from '@local/assets/svgs/icon-date.svg'
import {
	ForwardedRef,
	forwardRef,
	ReactNode,
	useEffect,
	useRef,
	useState,
} from 'react'
import {EMAIL_PATTERN, emailIsValid} from '@local/utils/string'
import {useTranslate} from '@local/hooks'

import 'react-date-picker/dist/DatePicker.css'
import PhoneNumberInput from '@local/ui/components/phoneNumberInput/PhoneNumberInput'

interface HotelEnquireSlideoutProps {
	successText?: string;
	successTitle?: string;
	isOpen: boolean;
	onClickClose: () => void;
	hotelEnquireQuestions: any;
	reservation: {
		arrivalDate: Date;
		departureDate: Date;
		guestCounts: string;
		stay: string;
	};
}

export const HotelEnquireSlideout = forwardRef(
	(
		{
			isOpen = false,
			onClickClose,
			successText = 'Thank you for your enquiry',
			successTitle = 'Enquiry Sent',
			reservation,
			hotelEnquireQuestions,
		}: HotelEnquireSlideoutProps,
		ref: ForwardedRef<HTMLDivElement>,
	) => {
		const locale = useTranslate()
		const [inputs, setInputs] = useState({
			firstname: '',
			lastname: '',
			email: '',
			phoneNumber: '',
			countryCode: '',
			dateFlexibility: '',
		})
		const [isLoading, setIsLoading] = useState<boolean>(false)
		const [isSuccess, setIsSuccess] = useState<boolean>(false)
		const [hasError, setHasError] = useState<boolean>(false)

		const formRef = useRef<HTMLFormElement>(null)

		const handleInput = (value: string, name: string) => {
			setInputs((prev) => ({...prev, [name]: value}))
		}
		const handleReset = () => {
			setInputs({
				firstname: '',
				lastname: '',
				email: '',
				phoneNumber: '',
				countryCode: '',
				dateFlexibility: '',
			})
		}

		const handleSubmit = async ({body}) => {
			setIsLoading(true)
			try {
				setIsSuccess(false)
				setHasError(false)

				if (!emailIsValid(body.email)) {
					throw new Error('InvalidEmail')
				}

				const res = await fetch('/api/hotel-enquire', {
					method: 'POST',
					body: JSON.stringify({
						emailAddress: body.email,
						phone: body.countryCode + body.phoneNumber,
						name: body.firstname + ' ' + body.lastname,
						guestCount: reservation.guestCounts,
						arrivalDate: reservation.arrivalDate.toLocaleDateString('en-GB', {day: '2-digit', month: 'short', year: 'numeric'}),
						departureDate: reservation.departureDate.toLocaleDateString('en-GB', {day: '2-digit', month: 'short', year: 'numeric'}),
						stayName: reservation.stay,
						dateFlexibility: body.dateFlexibility,
					}),
				})
				const result = await res.json()

				if (res.status === 200) {
					setIsSuccess(true)
					setIsLoading(false)
					return {status: 200}
				} else {
					setIsLoading(false)
					setHasError(true)
					return {errors: [result]}
				}
			} catch (error) {
				setIsLoading(false)
				setHasError(true)
			}
		}

		useEffect(() => {
			if (isSuccess) {
				setTimeout(() => {
					onClickClose()
					setIsSuccess(false)
				}, 2000)
			}
		}, [isSuccess])

		return (
			<Slideout
				ref={ref}
				variant='right'
				isOpen={isOpen}
				onClickClose={onClickClose}
				className='bg-grey-4 text-white relative z-[999] !px-0'>
				<div
					data-lenis-prevent
					data-slideout-open={isOpen}
					className='overflow-y-scroll min-h-screen h-fit z-[999] margin-px-1 flex flex-col scrollbar-hide'>
					<div className='sticky top-0 z-99 flex items-center justify-between w-full py-10 lg:py-20 bg-grey-4'>
						<h2 className='w-fit uppercase'>Enquire</h2>
						<Button
							small
							variant='icon'
							onClick={onClickClose}
							className='px-16 aspect-square'>
							<CloseSVG />
						</Button>
					</div>
					<Form
						ref={formRef}
						autoReset
						additionalReset={handleReset}
						onSubmit={handleSubmit}
						successMessage={
							<SuccessPanel title={successTitle} text={successText} />
						}
						errorClassName='bottom-8 text-center'
						successClassName='z-5 mt-0 -inset-1 bg-grey-4 w-[calc(100%+2px)]'
						className={cx(
							'flex flex-col h-fit w-full pt-5 gap-40 z-50',
							'[&_h3]:font-normal [&_h3]:text-18',
						)}>
						{reservation?.arrivalDate && reservation?.departureDate && (
							<div className='flex flex-col gap-6'>
								<ul className='flex flex-col gap-14 mt-20'>
									<TitleTextIcon
										title={locale('Arrival & Departure Date')}
										text={
											reservation?.arrivalDate.toLocaleDateString('en-US', {
												weekday: 'short',
												day: 'numeric',
												month: 'short',
											}) +
                      ' - ' +
                      reservation?.departureDate.toLocaleDateString('en-US', {weekday: 'short', day: 'numeric', month: 'short'})
										}>
										<DateSVG className='col-start-1 row-span-full w-16 h-16 text-white' />
									</TitleTextIcon>
									<TitleTextIcon
										title={locale('Night Count')}
										text={Math.ceil(
											(reservation?.departureDate.getTime() -
                        reservation?.arrivalDate.getTime()) /
                        (1000 * 60 * 60 * 24),
										).toString()}>
										<BedSVG className='col-start-1 row-span-full w-16 h-16 text-white ' />
									</TitleTextIcon>
									<TitleTextIcon
										title={locale('Guest Count')}
										text={reservation?.guestCounts}>
										<PersonSVG className='col-start-1 row-span-full w-16 h-16 text-white ' />
									</TitleTextIcon>
								</ul>
							</div>
						)}
						<div className='relative flex flex-col gap-20 w-full'>
							<h3>
								<Locale>How flexible are you with your departure date?</Locale>
							</h3>
							<div className='grid grid-cols-2 gap-10'>
								{hotelEnquireQuestions?.dateFlexibility.map((option) => (
									<label key={option} htmlFor={option}>
										<input
											type='radio'
											name='dateFlexibility'
											value={option}
											id={option}
											className='sr-only peer' />
										<span
											className={cx(
												'bg-transparent flex items-center justify-center h-48 text-12',
												'rounded-xxs border border-grey-2 peer-focus-visible:border-grey-1',
												'peer-checked:border-white transition-colors duration-200 ease-quart-out hover:cursor-pointer',
											)}>
											{option}
										</span>
									</label>
								))}
							</div>
						</div>
						<div className='grid grid-cols-2 gap-10'>
							<h3 className='col-span-2 mb-10'>
								<Locale>Your details so we can get in touch:</Locale>
							</h3>
							<Input
								required
								variant='dark'
								type='text'
								name='firstname'
								label={locale('Firstname')}
								value={inputs.firstname}
								onChange={(e) => handleInput(e.target.value, 'firstname')}
								className='h-48'
								inputClassName='h-full'
								errorClassName='z-10 bg-grey-4'
								labelClassName='bg-grey-4' />
							<Input
								required
								variant='dark'
								type='text'
								name='lastname'
								label={locale('Lastname')}
								value={inputs.lastname}
								onChange={(e) => handleInput(e.target.value, 'lastname')}
								className='h-48'
								inputClassName='h-full'
								errorClassName='z-10 bg-grey-4'
								labelClassName='bg-grey-4' />
							<Input
								required
								variant='dark'
								type='email'
								name='email'
								label='Email'
								value={inputs.email}
								onChange={(e) => handleInput(e.target.value, 'email')}
								onInvalid={() => setHasError(true)}
								pattern={EMAIL_PATTERN}
								hasError={hasError}
								className='h-48 col-span-2'
								inputClassName='h-full'
								errorClassName='z-10 bg-grey-4'
								labelClassName='bg-grey-4' />
							<PhoneNumberInput
								onChange={(value: string) => handleInput(value, 'phoneNumber')}
								onInvalid={() => setHasError(true)}
								hasError={hasError} />
						</div>

						<div className=' w-full'>
							<ButtonSpinner
								type='submit'
								small
								loading={isLoading}
								variant='primary-transparent'
								className='mt-auto w-full'>
								<Locale>Send</Locale>
							</ButtonSpinner>
						</div>
					</Form>
				</div>
			</Slideout>
		)
	},
)

const SuccessPanel = ({title, text}) => (
	<div className='flex flex-col items-start pt-40 w-full h-fit gap-14 bg-grey-4 normal-case text-left sticky top-86'>
		<h3 className='text-32 font-serif tracking-tight'>{title}</h3>
		<p className='text-12 text-grey-1 lg:span-mr-1-wide'>{text}</p>
	</div>
)

const TitleTextIcon = ({
	children,
	title,
	text,
}: {
	children: ReactNode;
	title: string;
	text: string;
}) => (
	<div className='grid grid-cols-[auto_1fr] justify-items-start items-start gap-y-5 gap-x-10'>
		{children}
		<h4 className='text-white text-11 mt-3 leading-none uppercase tracking-doubleWider col-start-2 row-start-1'>
			{title}:
		</h4>
		<SmallText textColor='grey-1' className='col-start-2 row-start-2 mt-0'>
			{text}
		</SmallText>
	</div>
)
